import type * as React from 'react';
import type {
  JobSearchResultResponse,
  JobRecruitSalaryExampleResponse,
} from '@doda/api-client/aspida/@types';
import {
  Text,
  Work,
  User,
  Location,
  Station,
  Income,
  Business,
} from '@doda/io-react/common';
import {
  textAjust,
  removeLF,
  joinText,
} from '@doda/common/src/utils/convTextUtil';
import styles from './index.module.scss';

const SALARY_EXAMPLE_TEXT_LIMIT = 40;
const CARD_A_SALARY_EXAMPLE_TEXT_LIMIT = 35;
/** 給与例テキスト生成 */
function buildSalaryExampleText(
  salaryExample: JobRecruitSalaryExampleResponse[],
  isCardTypeA: boolean
) {
  const textLimit = isCardTypeA
    ? CARD_A_SALARY_EXAMPLE_TEXT_LIMIT
    : SALARY_EXAMPLE_TEXT_LIMIT;
  const example1 = salaryExample.find((ex) => ex.index === 1);
  const example2 = salaryExample.find((ex) => ex.index === 2);
  if (example1 && example1.text) {
    // 給与例1を改行ごとに分割
    const example1Lines = example1.text.split(/\r\n|\n/);
    if (example1Lines.length >= 2) {
      // 2行分存在する
      const line1 = textAjust(example1Lines[0], textLimit);
      let line2 = textAjust(example1Lines[1], textLimit);
      if (example1Lines.length >= 3 || example2) {
        if (!line2.endsWith('...')) line2 += '...';
      }
      return (
        <>
          {line1}
          <br />
          {line2}
        </>
      );
    }
    if (example2 && example2.text) {
      // 給与例１は１行＆給与例２が存在する
      return (
        <>
          {textAjust(example1.text, textLimit)}
          <br />
          {textAjust(example2.text, textLimit)}
        </>
      );
    }
    // 給与例１が１行のみ
    return textAjust(example1.text, textLimit);
  }
  // 給与例2のみ
  if (example2 && example2.text) {
    return textAjust(example2.text, textLimit);
  }
  return '';
}

/**
 * 求人カード 詳細情報
 * @param detail 仕事内容
 * @param target 対象となる方概要
 * @param location 勤務地
 * @param station 駅名称
 * @param salary.salary 給与
 * @param salary.example 給与例リスト
 * @param business 事業内容
 * @param isCardTypeA 給与例テキスト生成時に使用
 */
export interface InfoListProps {
  detail:
    | JobSearchResultResponse['jobContentOutline']
    | JobSearchResultResponse['jobContentDetail'];
  target?: JobSearchResultResponse['targetMemberOutline'];
  location: JobSearchResultResponse['jobState'];
  station?: JobSearchResultResponse['stations'];
  salary: {
    salary: JobSearchResultResponse['salary'];
    example: JobSearchResultResponse['salaryExamples'];
  };
  business?: JobSearchResultResponse['businessContent'];
  isCardTypeA?: true;
}

export const InfoList = (props: InfoListProps) => {
  const {
    detail = '',
    target = '',
    location = '',
    station,
    salary: { salary = '', example },
    business = '',
    isCardTypeA = false,
  } = props;

  const adjustedDetail = detail?.slice(0, 60);
  const adjustedTarget = target?.slice(0, 60);
  const adjustedLocation = textAjust(removeLF(location), 60);
  const adjustedBusiness = textAjust(removeLF(business), 60);
  const adjustedSalary =
    example && example.length > 0
      ? buildSalaryExampleText(example, isCardTypeA)
      : textAjust(removeLF(salary), 60);

  return (
    <div>
      <ul className={styles['jobCard-infoList']}>
        {/* 仕事内容 */}
        {adjustedDetail && (
          <InfoListItem type="detail" content={adjustedDetail} />
        )}
        {/* 対象 */}
        {target && <InfoListItem type="target" content={adjustedTarget} />}
        {/* 勤務地 */}
        {adjustedLocation && (
          <InfoListItem type="location" content={adjustedLocation} />
        )}
        {/* 最寄り駅 */}
        {station && station.length > 0 && (
          <InfoListItem
            type="station"
            content={textAjust(joinText('、', ...station))}
          />
        )}
        {/* 給与 */}
        {adjustedSalary && (
          <InfoListItem type="salary" content={adjustedSalary} />
        )}
        {/* 事業 */}
        {business && (
          <InfoListItem type="business" content={adjustedBusiness} />
        )}
      </ul>
    </div>
  );
};

type InfoType = Exclude<keyof InfoListProps, 'isCardTypeA'>;

/**
 * 求人カード 詳細情報のタイトル
 */
interface InfoTitle {
  icon: React.ComponentType<{ color: 'blue'; size: 'XS' }>;
  text: string;
}
const infoMap: Record<InfoType, InfoTitle> = {
  detail: {
    icon: Work,
    text: '仕事',
  },
  target: {
    icon: User,
    text: '対象',
  },
  location: {
    icon: Location,
    text: '勤務地',
  },
  station: {
    icon: Station,
    text: '最寄駅',
  },
  salary: {
    icon: Income,
    text: '給与',
  },
  business: {
    icon: Business,
    text: '事業',
  },
};

/**
 * 求人カード 詳細情報のリスト
 * @param type 種類
 * @param content 内容
 */
interface InfoListItemProps {
  type: InfoType;
  content: string | React.ReactElement;
}

const InfoListItem = ({ type, content }: InfoListItemProps) => {
  const { icon: IconComponent, text } = infoMap[type];
  return (
    <li>
      <dl className={styles['jobCard-info']}>
        <dt className={styles['jobCard-info__title']}>
          <IconComponent color="blue" size="XS" />
          <Text
            as="span"
            color="blue"
            size="10"
            style={{ marginBlockStart: 1 }}
          >
            {text}
          </Text>
        </dt>
        <dd className={styles['jobCard-info__content']}>
          <Text as="p" color="black_pc" size="12">
            {content}
          </Text>
        </dd>
      </dl>
    </li>
  );
};
